import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
} from '@mui/material';
import { AttachFile, ReceiptLong } from '@mui/icons-material';
import { apiService } from '../services/api';
import { useProviderAgreementGate } from './agreements/ProviderAgreementSigningDialog';

interface EstimateAttachment {
  name: string;
  mimeType?: string;
  viewUrl?: string | null;
  missing?: boolean;
}

interface EstimateRow {
  _id: string;
  status: string;
  documentLabel?: string;
  description?: string;
  canAccept?: boolean;
  serviceType?: { name?: string; description?: string };
  providerId?: { firstName?: string; lastName?: string; businessName?: string };
  bid?: { _id: string; price: number; status: string; validUntil?: string };
  checkout?: {
    servicePrice: number;
    salesTaxAmount?: number;
    processingFee: number;
    totalCharge: number;
    platformCut: number;
  };
  scheduledDate?: string;
  providerEstimateAttachment?: EstimateAttachment | null;
}

const money = (n: number) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CustomerAdditionalServicesEstimates: React.FC = () => {
  const [rows, setRows] = useState<EstimateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [selected, setSelected] = useState<EstimateRow | null>(null);
  const [agreed, setAgreed] = useState(false);
  const { requestSignatures, agreementDialog } = useProviderAgreementGate();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.get<EstimateRow[]>('/additional-services/my-estimates');
      if (res.success && res.data) setRows(res.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load estimates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const [searchParams] = useSearchParams();
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const focusEstimates = searchParams.get('view') === 'estimates';

  useEffect(() => {
    if (!focusEstimates || loading || rows.length === 0) return;
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusEstimates, loading, rows.length]);

  const accept = async () => {
    if (!selected?.bid?._id || !agreed) return;
    const agreementsSigned = await requestSignatures({ bidId: selected.bid._id });
    if (!agreementsSigned) return;
    try {
      setAccepting(true);
      setError(null);
      const res = await apiService.post('/bids/accept', {
        bidId: selected.bid._id,
        customerAgreementAccepted: true,
      });
      if (!res.success) throw new Error(res.error || 'Failed to accept estimate');
      setSuccess('Estimate accepted. It is now an Invoice — your provider will schedule the job.');
      setSelected(null);
      setAgreed(false);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to accept estimate');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (rows.length === 0) {
    return focusEstimates ? (
      <Alert severity="info" sx={{ mb: 3 }}>
        You don&apos;t have any open estimates right now. The estimate may have expired or been withdrawn.
      </Alert>
    ) : null;
  }

  return (
    <Box ref={sectionRef} id="additional-services-estimates" sx={{ mb: 4, scrollMarginTop: 80 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReceiptLong color="primary" />
        Additional Services
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Estimates from your provider. Accept to create an Invoice; they will schedule the work.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stack spacing={2}>
        {rows.map((row) => {
          const providerName =
            row.providerId?.businessName ||
            `${row.providerId?.firstName || ''} ${row.providerId?.lastName || ''}`.trim() ||
            'Your provider';
          return (
            <Card key={row._id} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography fontWeight={700}>{row.serviceType?.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {providerName}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={row.documentLabel || 'Estimate'}
                    color={row.canAccept ? 'warning' : 'default'}
                    variant="outlined"
                  />
                </Stack>
                <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>
                  {row.description || row.serviceType?.description}
                </Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>
                  {money(row.bid?.price || 0)}
                </Typography>
                {row.checkout && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Card total about {money(row.checkout.totalCharge)}
                    {typeof row.checkout.salesTaxAmount === 'number'
                      ? ` (service ${money(row.checkout.servicePrice)} + sales tax ${money(row.checkout.salesTaxAmount)} + processing)`
                      : ' (includes payment processing)'}
                  </Typography>
                )}
                {row.providerEstimateAttachment?.viewUrl && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1.5 }}
                    startIcon={<AttachFile />}
                    href={row.providerEstimateAttachment.viewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View estimate document
                  </Button>
                )}
                {row.providerEstimateAttachment?.missing && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    The estimate document isn't available right now. Ask your provider to send it again.
                  </Typography>
                )}
                {row.canAccept && (
                  <Button variant="contained" sx={{ mt: 2 }} onClick={() => { setSelected(row); setAgreed(false); }}>
                    Review &amp; Accept
                  </Button>
                )}
                {row.status === 'accepted' && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                    Accepted — waiting for provider to schedule
                  </Typography>
                )}
                {row.scheduledDate && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Scheduled: {new Date(row.scheduledDate).toLocaleDateString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Dialog open={!!selected} onClose={() => !accepting && setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>Accept Estimate</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Accept <strong>{selected?.serviceType?.name}</strong> for{' '}
            <strong>{money(selected?.bid?.price || 0)}</strong>? This becomes an Invoice and your provider can schedule
            the job. You will be charged when the job is completed.
          </Typography>
          {selected?.providerEstimateAttachment?.viewUrl && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachFile />}
              href={selected.providerEstimateAttachment.viewUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mb: 2 }}
            >
              View estimate document ({selected.providerEstimateAttachment.name})
            </Button>
          )}
          <FormControlLabel
            control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
            label="I agree to the Customer Service Agreement and this Additional Services estimate."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} disabled={accepting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={accept} disabled={!agreed || accepting}>
            {accepting ? <CircularProgress size={20} /> : 'Accept Estimate'}
          </Button>
        </DialogActions>
      </Dialog>

      {agreementDialog}
    </Box>
  );
};

export default CustomerAdditionalServicesEstimates;
